import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { ArrowLeft } from 'lucide-react'
import { getProjectById, updateProjectConstruction, updateProjectSupplier, getProjectOptions } from '@/lib/api/projects'
import { showErrorToast } from '@/lib/toast'
import type { Project, ConstructionType } from '@/types'

const supplierSchema = z.object({
  supplier: z.string().min(1, 'Sélectionnez un fournisseur'),
  modelReference: z.string().optional(),
})

type SupplierFormData = z.infer<typeof supplierSchema>

interface ProjectOptions {
  constructionTypes: Array<{ value: ConstructionType; label: string; description: string; icon: string }>
  suppliers: Array<{ id: string; name: string; isDefault: boolean }>
}

export default function ProjectConfigEditPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { id: projectId } = useParams<{ id: string }>()

  const [project, setProject] = useState<Project | null>(null)
  const [options, setOptions] = useState<ProjectOptions | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSavingConstructionType, setIsSavingConstructionType] = useState(false)
  const [isSavingSupplier, setIsSavingSupplier] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<SupplierFormData>({
    resolver: zodResolver(supplierSchema),
    mode: 'onChange',
    defaultValues: {
      supplier: '',
      modelReference: '',
    },
  })

  // Load project and options
  useEffect(() => {
    if (!projectId) {
      navigate('/home')
      return
    }

    async function loadData() {
      try {
        const [projectData, optionsData] = await Promise.all([
          getProjectById(projectId!),
          getProjectOptions(),
        ])

        if (!projectData) {
          navigate('/home')
          return
        }

        setProject(projectData)
        setOptions({
          constructionTypes: optionsData.constructionTypes,
          suppliers: optionsData.suppliers,
        })

        // Set form values
        setValue('supplier', projectData.supplier || '', { shouldValidate: true })
        setValue('modelReference', projectData.modelReference || '')

        // Auto-select default supplier if none selected
        if (!projectData.supplier) {
          const defaultSupplier = optionsData.suppliers.find((s) => s.isDefault)
          if (defaultSupplier) {
            setValue('supplier', defaultSupplier.id, { shouldValidate: true })
          }
        }
      } catch (error) {
        console.error('Error loading data:', error)
        showErrorToast('Erreur de chargement')
        navigate('/home')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [projectId, navigate, setValue])

  const handleConstructionTypeSelect = async (type: ConstructionType) => {
    if (!projectId || isSavingConstructionType) return

    setIsSavingConstructionType(true)
    try {
      // TODO: Replace with V2 API endpoint when available
      // const updatedProject = await updateProjectConstructionV2(projectId, { constructionType: type })
      const updatedProject = await updateProjectConstruction(projectId, {
        constructionType: type,
      })
      setProject(updatedProject)
    } catch (error) {
      console.error('Error updating construction type:', error)
      showErrorToast('Erreur de sauvegarde du type de travaux')
    } finally {
      setIsSavingConstructionType(false)
    }
  }

  const onSubmitSupplier = async (data: SupplierFormData) => {
    if (!projectId) return

    setIsSavingSupplier(true)
    try {
      // TODO: Replace with V2 API endpoint when available
      // const updatedProject = await updateProjectSupplierV2(projectId, { supplier: data.supplier, modelReference: data.modelReference })
      const updatedProject = await updateProjectSupplier(projectId, {
        supplier: data.supplier,
        modelReference: data.modelReference,
      })
      setProject(updatedProject)
      // Navigate back after successful save
      handleBack()
    } catch (error) {
      console.error('Error updating supplier:', error)
      showErrorToast('Erreur de sauvegarde du fournisseur')
    } finally {
      setIsSavingSupplier(false)
    }
  }

  const handleBack = () => {
    const from = (location.state as { from?: string } | null)?.from
    if (from) {
      navigate(from)
    } else {
      navigate(`/projects/${projectId}`)
    }
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Chargement...</div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  if (!project || !options) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12 text-muted-foreground">
            Erreur de chargement
          </div>
        </div>
      </DashboardLayout>
    )
  }

  const selectedConstructionType = project.constructionType
  const selectedSupplier = watch('supplier')
  const isCompany = project.client?.isCompany ?? false

  // SOLY-SUPPLIER-001: the backend serves no suppliers to an organization without the
  // project_supplier module, and that emptiness IS the signal. Reading the module list here
  // too would give the same fact two sources of truth; the API already owns it.
  const hasSupplierSection = options.suppliers.length > 0

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Configuration du projet</h1>
            <p className="text-sm text-muted-foreground">
              {project.reference} {project.name ? `• ${project.name}` : ''}
            </p>
          </div>
        </div>

        {/* Section 1: Construction Type */}
        <div className="space-y-3">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="text-2xl">📋</span>
              Type de travaux
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Le type de travaux détermine la TVA appliquée sur les éléments du projet
            </p>
          </div>

          <div className="space-y-2">
            {options.constructionTypes.map((type) => {
              const isDisabled = type.value === 'co_contractor' && !isCompany
              const isSelected = selectedConstructionType === type.value

              return (
                <button
                  key={type.value}
                  data-testid={`construction-type-btn-${type.value}`}
                  onClick={() => !isDisabled && handleConstructionTypeSelect(type.value)}
                  disabled={isDisabled || isSavingConstructionType}
                  className={`
                    group relative w-full p-3 bg-card border-2 rounded-lg 
                    text-left transition-all duration-200
                    ${isDisabled 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:border-primary hover:shadow-md cursor-pointer'
                    }
                    ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border'
                    }
                  `}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">
                      {type.icon}
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-semibold mb-0.5">
                        {type.label}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {type.description}
                      </p>
                      
                      {isDisabled && (
                        <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                          💡 Uniquement pour clients professionnels
                        </p>
                      )}
                    </div>

                    {isSelected && (
                      <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center shrink-0">
                        <span className="text-white text-sm">✓</span>
                      </div>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {hasSupplierSection ? (
        <>
        <Separator className="my-6" />

        {/* Section 2: Supplier */}
        <form onSubmit={handleSubmit(onSubmitSupplier)} className="space-y-3">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="text-2xl">🏭</span>
              Fabrication
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Sélectionnez le fournisseur et le modèle de référence
            </p>
          </div>

          <div className="space-y-4">
            {/* Supplier selection */}
            <div className="space-y-3">
              <Label>Fournisseur *</Label>
              <div className="space-y-2">
                {options.suppliers.map((supplier) => (
                  <label
                    key={supplier.id}
                    className={`
                      flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer
                      transition-all duration-200
                      hover:border-primary
                      ${
                        selectedSupplier === supplier.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      value={supplier.id}
                      {...register('supplier')}
                      className="w-4 h-4"
                    />
                    <span className="flex-1 font-medium">{supplier.name}</span>
                    {supplier.isDefault && (
                      <span className="text-xs text-muted-foreground">
                        Par défaut
                      </span>
                    )}
                    {selectedSupplier === supplier.id && (
                      <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">✓</span>
                      </div>
                    )}
                  </label>
                ))}
              </div>
              {errors.supplier && (
                <p className="text-sm text-red-600">{errors.supplier.message}</p>
              )}
            </div>

            {/* Model reference */}
            <div className="space-y-2">
              <Label htmlFor="modelReference">Modèle / Référence (optionnel)</Label>
              <Input
                id="modelReference"
                {...register('modelReference')}
                placeholder="Ex: Largo, Classic, AV2050..."
              />
              <p className="text-xs text-muted-foreground">
                💡 Utile pour retrouver le projet plus tard
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              disabled={isSavingSupplier}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={!isValid || isSavingSupplier || !selectedConstructionType}
            >
              {isSavingSupplier ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </form>
        </>
        ) : (
          /* The action bar lives inside that form, so hiding it would leave the page with no
             way out but the header arrow. The work type saves on click, so there is nothing
             left to submit -- just a way back. */
          <div className="flex items-center justify-end pt-2">
            <Button type="button" onClick={handleBack}>
              Terminé
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
